import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import {
  AuthModal,
  AuthCredential,
  AuthOTPCredential,
} from "./auth-modal/auth-modal";
import { take, tap, of, Observable } from "rxjs";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";

const baseUrl = environment.baseUrl;
const apiRoutes = {
  login: () => `${baseUrl}/assurance/auth/login/`,
  verifyUser: () => `${baseUrl}/accounts/verify-user/`,
  loginWithOTP: () => `${baseUrl}/accounts/login/`,
  getNewAccess_token: () => `${baseUrl}/accounts/api/token/refresh/`,
  resendOtp: () => `${baseUrl}/accounts/resend-otp/`,
  logoutAndBlockToken: () => `${baseUrl}/accounts/logout/`,
  forgotPassword: () => `${baseUrl}/auth/forgotPassword/`,
  resetPassword: () => `${baseUrl}/assurance/auth/reset-password/`,
};

interface LoginData {
  email: string;
  userID: number;
  _token: string;
  _retoken: string;
  user_name?: string;
  provider_id?: number;
  provider_name?: string;
  role?: string;
  provider_user_id?: number;
}
@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    private HttpClient: HttpClient,
    private router: Router,
    private toaster: ToastrService
  ) {}

  verifyUser(body: AuthCredential) {
    return this.HttpClient.post<any>(apiRoutes.verifyUser(), body);
  }

  loginWith_OTP(body: AuthOTPCredential) {
    return this.HttpClient.post<any>(apiRoutes.loginWithOTP(), body).pipe(
      tap((res) => {
        const userData: LoginData = {
          email: res.user_email,
          userID: res.user_id,
          _token: res.token.access,
          _retoken: res.token.refresh,
          user_name: res.user_name,
        };
        this.adminAuthentication(userData);
      })
    );
  }

  setNewAccess_token(refresh_token: string) {
    const body = { refresh: refresh_token };
    this.HttpClient.post<any>(apiRoutes.getNewAccess_token(), body).subscribe(
      (res) => {
        const logindata: any = localStorage.getItem("loginUser");
        const loginUser = JSON.parse(logindata);
        let with_updateAccess_token = JSON.stringify({
          ...loginUser,
          _token: res.access,
        });
        localStorage.setItem("loginUser", with_updateAccess_token);
        this.toaster.warning("Please Refresh the page");
      },
      (err) => {
        
        this.toaster.error("please login again", "Session Expired");
        this.logout();
      }
    );
  }

  login(body: AuthCredential) {
    return this.HttpClient.post<any>(apiRoutes.login(), body).pipe(
      tap((res) => {
        if (res.status === 'first_login') return;
        const userData: LoginData = {
          email: res.user_email,
          userID: res.user_id,
          _token: res.token.access,
          _retoken: res.token.refresh,
          user_name: res.user_name,
          provider_id: res.provider?.provider_id,
          provider_name: res.provider?.provider_name,
          role: res.provider?.role,
          provider_user_id: res.provider?.provider_user_id,
        };
        this.adminAuthentication(userData);
      })
    );
  }

  resetPassword(body: { uid: string; token: string; new_password: string; confirm_newpassword: string }) {
    return this.HttpClient.post<any>(apiRoutes.resetPassword(), body);
  }

  resendOtp(data: any) {
    return this.HttpClient.post<any>(apiRoutes.resendOtp(), data);
  }

  logoutAndBlockToken() {
    const userSession = this.getUserSession();
    if (!userSession?._retoken) {
      return of("No active session to invalidate.");
    }
    return this.HttpClient.post<any>(apiRoutes.logoutAndBlockToken(), {
      refresh: userSession._retoken,
    });
  }

  logout() {
    this.logoutAndBlockToken().subscribe({
      next: (res: any) => console.log("Token invalidated", res),
      error: (err: any) => console.error("Token invalidation failed", err),
    });
    this.cleanUserSession();
  }

  cleanUserSession() {
    localStorage.removeItem("loginUser");
    localStorage.removeItem("token");
    this.router.navigate(["/auth/login"]);
  }

  getUserSession() {
    const userProfile: any = localStorage.getItem("loginUser");
    return JSON.parse(userProfile);
  }

  adminAuthentication(data: LoginData) {
    const { email, userID, _token, _retoken, user_name, provider_id, provider_name, role, provider_user_id } = data;
    const User = new AuthModal(email, userID, _token, _retoken, user_name, provider_id, provider_name, role, provider_user_id);
    let loginUser = JSON.stringify(User);
    localStorage.setItem("loginUser", loginUser);
    localStorage.setItem("token", _token);
  }

  getProviderId(): number | null {
    const session = this.getUserSession();
    return session?.provider_id ?? null;
  }

  getProviderRole(): string | null {
    const session = this.getUserSession();
    return session?.role ?? null;
  }

  getProviderUserId(): number | null {
    const session = this.getUserSession();
    return session?.provider_user_id ?? null;
  }

  isIkooAdmin(): boolean {
    const session = this.getUserSession();
    return session?.role === 'ikoo_admin';
  }

  forgotPassword(body: any) {
    return this.HttpClient.post<any>(apiRoutes.forgotPassword(), body);
  }
}
